import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContractsId200,
  GetApiV1ThreeLetterCodePartnerAdminsContractPlans200,
} from '@fukuri-admins/apis/client/gen/model';
import { isLastDayOfMonth } from '@fukuri-admins/utils/common/date';
import type { EditClientContractTermPresentation } from '../../presentations';

/**
 * converter が API レスポンスから導出できる Presentation Props のみを対象にする
 *
 * onSubmit / onCancel / isButtonDisabled は hooks 層が組み立てるハンドラ・状態であり、
 * API レスポンスからは導出できないため Omit で除外する。
 */
type PresentationProps = Omit<
  React.ComponentProps<typeof EditClientContractTermPresentation>,
  'onSubmit' | 'onCancel' | 'isButtonDisabled'
>;

type Args = {
  /** API レスポンス（useFetch の data をそのまま受け取る） */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContractsId200;
  /** 契約プラン一覧データ */
  contractPlansData: GetApiV1ThreeLetterCodePartnerAdminsContractPlans200;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * 企業契約期間データを EditClientContractTerm の Presentation Props に変換する
 *
 * InternalAdmin 準拠の表示項目に変更。
 * - clientOptions: 単一 Resource レスポンスの client から 1 件のみの配列を生成（一覧 API を呼び戻すことは禁止）
 * - contractPlanOptions: discontinued_at === 0 のものだけフィルタするが、現在紐づくプランが
 *   フィルタ後の一覧に無い場合は例外的に先頭へ補完する（PEN-1159）。Radix Select は value に一致する
 *   SelectItem が描画されていないとトリガー表示が空になるため。
 *   一覧に無い理由は廃止済みとは限らない（一覧 API は page 未指定で 1 ページ目しか取得しないため、
 *   提供中でも 2 ページ目以降のプランは一覧外になる）ので、`（廃止済み）` は廃止済みのときだけ付ける
 * - endedOnDisabledDate: サーバー側が月末日以外の終了日を受け付けないため、月末日以外を選択不可にする（PEN-1158）
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  contractPlansData,
}) => {
  const contractTerm = data.client_contract;
  const currentPlan = contractTerm.contract_plan;

  const availablePlanOptions = contractPlansData.contract_plans
    .filter(plan => plan.discontinued_at === 0)
    .map(plan => ({ value: String(plan.id), label: plan.name }));
  const currentPlanValue = String(currentPlan.id);
  const currentPlanLabel =
    currentPlan.discontinued_at === 0 ? currentPlan.name : `${currentPlan.name}（廃止済み）`;
  const contractPlanOptions = availablePlanOptions.some(({ value }) => value === currentPlanValue)
    ? availablePlanOptions
    : [{ value: currentPlanValue, label: currentPlanLabel }, ...availablePlanOptions];

  return {
    clientOptions: [{ value: String(contractTerm.client.id), label: contractTerm.client.name }],
    contractPlanOptions,
    isStartedOnDisabled: !contractTerm.is_started_on_editable,
    isContractPlanDisabled: !contractTerm.is_contract_plan_editable,
    endedOnFromDate: new Date(contractTerm.ended_on_min),
    endedOnDisabledDate: date => !isLastDayOfMonth(date),
  };
};
